import { Divider } from "@mui/material";
import React from "react";
import Article from "../../components/Article";
import TextLink from "../../components/TextLink";
import appsyncDdbImage from "../../assets/images/appsync-ddb.png";

export const AppSyncDynamoSingleTableDesignDetails = {
  articleTitle: "AppSync and DynamoDB Single Table Design",
  articleDate: "2025-05-29",
  articleAuthor: "Harrison Cannon",
  articleImage: appsyncDdbImage,
  articleBody: "Learn how to model a GraphQL API with AppSync and DynamoDB using Single Table Design.",
  articleSlug: "appsync-dynamodb-single-table-design"
};

function AppSyncDynamoSingleTableDesign() {
  function ArticleContent() {
    return (
      <div>
        <h2>Why this pattern works</h2>

        <p>
          Building APIs with <TextLink url="https://aws.amazon.com/appsync/" text="AWS AppSync" /> and <TextLink url="https://aws.amazon.com/dynamodb/" text="Amazon DynamoDB" /> is a great fit for event-driven and serverless systems. AppSync gives you managed GraphQL, while DynamoDB gives you predictable low-latency reads and writes at scale.
        </p>

        <p>
          The biggest shift for teams coming from relational systems is data modelling. Instead of creating one table per entity, you design access patterns first and store related entities in one table using composite keys.
        </p>

        <Divider className="divider" />

        <h2>Sample domain and access patterns</h2>

        <p>
          In this article, we use a simple issue tracker with <code>Project</code>, <code>Ticket</code> and <code>Comment</code> entities. We need to support these queries:
        </p>

        <ul>
          <li>Get project by ID</li>
          <li>List tickets by project</li>
          <li>Get ticket by ID</li>
          <li>List comments by ticket sorted by creation time</li>
        </ul>

        <p>
          A single-table model can represent this with a shared primary key strategy:
        </p>

        <pre>
          <code>
            {`[
  {
    "PK": "PROJECT#<projectId>",
    "SK": "METADATA#PROJECT"
  },
  {
    "PK": "PROJECT#<projectId>",
    "SK": "TICKET#<ticketId>"
  },
  {
    "PK": "TICKET#<ticketId>",
    "SK": "COMMENT#<isoTimestamp>#<commentId>"
  }
]`}
          </code>
        </pre>

        <p>
          This approach is important because it enables you to efficiently fetch the data you need with targeted queries, eliminating the need for expensive full-table scans. By designing your schema around access patterns, you ensure consistent performance and scalability as your dataset grows.
        </p>
   

        <Divider className="divider" />

        <h2>GraphQL schema</h2>

        <p>
          Keep your schema focused on client use-cases, not the physical table layout:
        </p>

        <pre>
          <code>
            {`type Project {
  id: ID!
  name: String!
}

type Ticket {
  id: ID!
  projectId: ID!
  title: String!
  status: String!
}

type Comment {
  id: ID!
  ticketId: ID!
  body: String!
  createdAt: AWSDateTime!
}

type Query {
  getProject(projectId: ID!): Project
  listTicketsByProject(projectId: ID!, limit: Int, nextToken: String): [Ticket!]!
  listCommentsByTicket(ticketId: ID!, limit: Int, nextToken: String): [Comment!]!
}

type Mutation {
  createTicket(projectId: ID!, title: String!): Ticket!
  addComment(ticketId: ID!, body: String!): Comment!
}`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>CDK constructs in TypeScript</h2>

        <p>
          The snippet below provisions a DynamoDB table, an AppSync API, and a DynamoDB data source using AWS CDK v2:
        </p>

        <pre>
          <code>
            {`import * as cdk from "aws-cdk-lib";
import { Construct } from "constructs";
import * as dynamodb from "aws-cdk-lib/aws-dynamodb";
import * as appsync from "aws-cdk-lib/aws-appsync";
import * as iam from "aws-cdk-lib/aws-iam";

export class IssueTrackerApiStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props?: cdk.StackProps) {
    super(scope, id, props);

    const table = new dynamodb.Table(this, "IssueTable", {
      partitionKey: { name: "PK", type: dynamodb.AttributeType.STRING },
      sortKey: { name: "SK", type: dynamodb.AttributeType.STRING },
      billingMode: dynamodb.BillingMode.PAY_PER_REQUEST,
      pointInTimeRecovery: true
    });

    table.addGlobalSecondaryIndex({
      indexName: "GSI1",
      partitionKey: { name: "GSI1PK", type: dynamodb.AttributeType.STRING },
      sortKey: { name: "GSI1SK", type: dynamodb.AttributeType.STRING },
      projectionType: dynamodb.ProjectionType.ALL
    });

    const api = new appsync.GraphqlApi(this, "IssueApi", {
      name: "issue-tracker-api",
      schema: appsync.SchemaFile.fromAsset("graphql/schema.graphql"),
      authorizationConfig: {
        defaultAuthorization: { authorizationType: appsync.AuthorizationType.IAM }
      },
      xrayEnabled: true
    });

    const ddbDataSource = api.addDynamoDbDataSource("IssueTableDataSource", table);

    ddbDataSource.grantPrincipal.addToPrincipalPolicy(
      new iam.PolicyStatement({
        actions: ["dynamodb:Query", "dynamodb:GetItem", "dynamodb:PutItem", "dynamodb:UpdateItem"],
        resources: [table.tableArn, \`\${table.tableArn}/index/*\`]
      })
    );
  }
}`}
          </code>
        </pre>

        <p>
          For larger APIs, split resolvers into separate CDK constructs per domain area, such as projects, tickets and comments.
        </p>

        <Divider className="divider" />

        <h2>Resolver examples</h2>

        <p>
          With AppSync JavaScript resolvers, map GraphQL fields to key-based DynamoDB operations.
        </p>

        <h3>Get Project</h3>
        <pre>
          <code>
            {`import { util } from "@aws-appsync/utils";

export function request(ctx) {
  return {
    operation: "GetItem",
    key: util.dynamodb.toMapValues({
      PK: \`PROJECT#\${ctx.args.projectId}\`,
      SK: "METADATA#PROJECT"
    })
  };
}

export function response(ctx) {
  return ctx.result;
}`}
          </code>
        </pre>

        <h3>List Tickets By Project</h3>
        <pre>
          <code>
            {`import { util } from "@aws-appsync/utils";

export function request(ctx) {
  return {
    operation: "Query",
    query: {
      expression: "PK = :pk AND begins_with(SK, :ticketPrefix)",
      expressionValues: util.dynamodb.toMapValues({
        ":pk": \`PROJECT#\${ctx.args.projectId}\`,
        ":ticketPrefix": "TICKET#"
      })
    },
    limit: ctx.args.limit ?? 25,
    nextToken: ctx.args.nextToken
  };
}

export function response(ctx) {
  return ctx.result.items;
}`}
          </code>
        </pre>

        <h3>Add Comment</h3>
        <pre>
          <code>
            {`import { util } from "@aws-appsync/utils";

export function request(ctx) {
  const now = util.time.nowISO8601();
  const commentId = util.autoId();

  return {
    operation: "PutItem",
    key: util.dynamodb.toMapValues({
      PK: \`TICKET#\${ctx.args.ticketId}\`,
      SK: \`COMMENT#\${now}#\${commentId}\`
    }),
    attributeValues: util.dynamodb.toMapValues({
      id: commentId,
      ticketId: ctx.args.ticketId,
      body: ctx.args.body,
      createdAt: now,
      entityType: "COMMENT"
    })
  };
}

export function response(ctx) {
  return ctx.result;
}`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Design tips for production</h2>

        <ul>
          <li>Start with access patterns and write test fixtures that validate each query path.</li>
          <li>Add entity type attributes such as <code>entityType</code> to simplify filtering and debugging.</li>
          <li>Use GSIs only when a query cannot be served by the base table keys.</li>
          <li>Protect writes with conditional expressions to avoid accidental overwrites.</li>
          <li>Track consumed capacity and resolver latency in CloudWatch and X-Ray.</li>
        </ul>

        <Divider className="divider" />

        <h2>Final thoughts</h2>

        <p>
          AppSync plus DynamoDB single-table design gives you a scalable GraphQL backend with minimal operational overhead. The key is to design your table around real queries and keep resolver logic deterministic and key-driven.
        </p>

        <p>
          If you want to go further, add subscriptions for realtime ticket updates and pipe DynamoDB Streams into EventBridge for asynchronous workflows.
        </p>
      </div>
    );
  }

  return <Article
    article={AppSyncDynamoSingleTableDesignDetails}
    content={ArticleContent}
  />;
}

export default AppSyncDynamoSingleTableDesign;
